import { useCallback } from 'react';

import ChangePasswordPanel from 'components/login/ChangePasswordPanel.component';
import EmailPanel from 'components/login/EmailPanel.component';
import ResetErrorPanel from 'components/login/ResetErrorPanel.component';
import STORE from 'config/store.singleton';
import LINK from 'constants/link.const';
import S from 'constants/string.const';
import useAfn from 'hooks/useAfn.hook';
import { useNavFor } from 'hooks/useNav.hook';
import useStateWhileMounted from 'hooks/useStateWhileMounted.hook';
import { useTranslation } from 'providers/GILT.provider';
import { withRcmPasswordReset } from 'utils/cognito.util';
import fromAnyToErrorMessage from 'utils/converters/any/fromAnyToErrorMessage.util';
import logger from 'utils/logger.util';

import CN from './ForgotPasswordPage.module.scss';

const CTAG = 'ForgotPasswordPage';
const L = logger(CTAG);

const determineInputState = (t) =>
  Object.freeze({
    emailInput: t('forgotPassword.inputStatus.noEmail'),
    emailError: t('forgotPassword.inputStatus.emailError'),
    codeInput: t('forgotPassword.inputStatus.noCode'),
    codeError: t('forgotPassword.inputStatus.codePasswordError'),
    done: t('forgotPassword.inputStatus.successfullyChangePassword'),
  });

const ForgotPasswordPage = ({ sendVerificationCode, setPassword }) => {
  const { t } = useTranslation();
  const nav = useNavFor('ForgotPasswordPage');

  const STATE = determineInputState(t);

  const [email, setEmail] = useStateWhileMounted(S.empty);
  const [state, setState] = useStateWhileMounted(STATE.emailInput);
  const [error, setError] = useStateWhileMounted(S.empty);

  const handleEmailError = useCallback(() => void setState(STATE.codeInput), [STATE.codeInput, setState]);
  const handleCodeError = useCallback(() => void setState(STATE.emailInput), [STATE.emailInput, setState]);

  const [{ isLoading: isEmailLoading }, handleEmailInput] = useAfn(
    async ({ email }) => {
      try {
        if (isEmailLoading) {
          L.warn('handleEmailInput()', { isEmailLoading, email });
          return;
        }
        setEmail(email);
        await sendVerificationCode(email);
        setState(STATE.codeInput);
        setError(S.empty);
      } catch (e) {
        L.warn('handleEmail()', e);
        setState(STATE.emailError);
        setError(e.code === 'UserNotFoundException' ? t('forgotPassword.userNotFound') : fromAnyToErrorMessage(e));
      }
    },
    [setEmail, sendVerificationCode, setState, STATE.codeInput, STATE.emailError, setError, t],
  );

  const [{ isLoading: isPassLoading }, handleCodeInput] = useAfn(
    async ({ code, pass }) => {
      try {
        if (isPassLoading) {
          L.warn('handleCodeInput()', { isPassLoading, code });
          return;
        }
        await setPassword(email, code, pass);
        setError(S.empty);
        setState(S.done);
        nav.push({ pathname: LINK.login });
      } catch (e) {
        L.warn('handleCodeInput()', e);
        setState(STATE.codeError);
        setError(fromAnyToErrorMessage(e));
      }
    },
    [STATE.codeError, email, nav, setError, setPassword, setState],
  );

  const isLoading = isEmailLoading || isPassLoading;

  L.info('()', state);

  return (
    <div data-bem="ForgotPasswordPage" className={CN.component}>
      <div className={CN.illustrationTop} />
      <div className={CN.illustrationBottom} />
      <div className={CN.panel} data-bem="ForgotPasswordPage__body">
        {state === STATE.emailInput && <EmailPanel tag={CTAG} onNext={handleEmailInput} isLoading={isLoading} />}
        {state === STATE.emailError && <ResetErrorPanel tag={CTAG} onNext={handleEmailError} error={error} subtitle={t('forgotPassword.resetErrorModal.description')} />}
        {state === STATE.codeInput && <ChangePasswordPanel onNext={handleCodeInput} isLoading={isLoading} />}
        {state === STATE.codeError && <ResetErrorPanel tag={CTAG} onNext={handleCodeError} error={error} />}
        {state === STATE.codeError && <div />}
      </div>
    </div>
  );
};

const ForgotPasswordPageHoC = withRcmPasswordReset(ForgotPasswordPage, STORE);

export default ForgotPasswordPageHoC;
